/**
 * Cleanup for open-chat user prompts — obvious typos + readable formatting.
 * Not a full spellchecker; never invents words from fuzzy distance.
 */

const OBVIOUS_TYPOS: Record<string, string> = {
  // Common English
  teh: 'the',
  thye: 'the',
  hte: 'the',
  adn: 'and',
  nad: 'and',
  taht: 'that',
  whcih: 'which',
  wiht: 'with',
  wtih: 'with',
  nto: 'not',
  dont: "don't",
  doesnt: "doesn't",
  wont: "won't",
  cant: "can't",
  isnt: "isn't",
  wasnt: "wasn't",
  werent: "weren't",
  havent: "haven't",
  hasnt: "hasn't",
  wouldnt: "wouldn't",
  couldnt: "couldn't",
  shouldnt: "shouldn't",
  recieve: 'receive',
  recieved: 'received',
  seperate: 'separate',
  seperately: 'separately',
  definately: 'definitely',
  occured: 'occurred',
  occurence: 'occurrence',
  sucess: 'success',
  sucessful: 'successful',
  neccessary: 'necessary',
  accomodate: 'accommodate',
  adress: 'address',
  untill: 'until',
  tommorow: 'tomorrow',
  begining: 'beginning',
  enviroment: 'environment',
  goverment: 'government',
  buisness: 'business',
  buisnesses: 'businesses',
  lenght: 'length',
  hieght: 'height',
  widht: 'width',
  thier: 'their',
  thiers: 'theirs',
  wierd: 'weird',
  truely: 'truly',
  realy: 'really',
  allways: 'always',
  alwoas: 'always',
  aways: 'always',
  becuase: 'because',
  beacuse: 'because',
  becasue: 'because',
  aobut: 'about',
  abotu: 'about',
  peice: 'piece',
  peices: 'pieces',
  writting: 'writing',
  writen: 'written',
  readalble: 'readable',
  readible: 'readable',
  // Prompt / product UI
  rpompt: 'prompt',
  promot: 'prompt',
  promopt: 'prompt',
  prmopt: 'prompt',
  prmopts: 'prompts',
  promts: 'prompts',
  chatn: 'chat',
  scrool: 'scroll',
  croll: 'scroll',
  bttom: 'bottom',
  bototm: 'bottom',
  dewtails: 'details',
  detials: 'details',
  detial: 'detail',
  obvouse: 'obvious',
  obviouse: 'obvious',
  wawte: 'waste',
  neeidng: 'needing',
  needin: 'needing',
  hsould: 'should',
  shoudl: 'should',
  shold: 'should',
  fdeature: 'feature',
  feautre: 'feature',
  feauture: 'feature',
  recommedation: 'recommendation',
  recommenation: 'recommendation',
  reccomend: 'recommend',
  reommend: 'recommend',
  evalute: 'evaluate',
  evalution: 'evaluation',
  discription: 'description',
  descirption: 'description',
  descritpion: 'description',
  attachement: 'attachment',
  attatch: 'attach',
  availible: 'available',
  availabe: 'available',
  responsability: 'responsibility',
  independant: 'independent',
  dependant: 'dependent',
  refering: 'referring',
  prefered: 'preferred',
  transfered: 'transferred',
  // Task / CEO vocab
  prioirty: 'priority',
  priorty: 'priority',
  scheudle: 'schedule',
  shedule: 'schedule',
  superbase: 'Supabase',
  supabasee: 'Supabase',
};

function applyCase(source: string, replacement: string): string {
  if (!source) return replacement;
  if (source === source.toUpperCase() && source.length > 1) {
    return replacement.toUpperCase();
  }
  if (source[0] === source[0].toUpperCase() && source.slice(1) === source.slice(1).toLowerCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

function fixObviousTypos(text: string): string {
  return text.replace(/\b([A-Za-z']+)\b/g, (word) => {
    const key = word.toLowerCase();
    const fix = OBVIOUS_TYPOS[key];
    if (!fix) return word;
    return applyCase(word, fix);
  });
}

/** Normalize whitespace / punctuation so dumps read cleanly in chat. */
function formatForReadability(text: string): string {
  let t = text
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    // Space after sentence punctuation when missing: "done.This" → "done. This"
    .replace(/([.!?])([A-Za-z])/g, '$1 $2')
    // Space after commas/colons/semicolons when missing
    .replace(/([,:;])([A-Za-z0-9])/g, '$1 $2')
    // No space before punctuation
    .replace(/\s+([,.;:!?])/g, '$1')
    // Collapse " ." leftovers
    .replace(/\s+\./g, '.')
    .trim();

  // Capitalize first letter of the prompt and after .!? + space/newline
  t = t.replace(/(^|[.!?]\s+|\n)([a-z])/g, (_, prefix: string, ch: string) => {
    return `${prefix}${ch.toUpperCase()}`;
  });

  return t.trim();
}

/**
 * Make a user chat prompt readable: obvious typos + light formatting.
 * Used whenever open-chat stores/sends a user message.
 */
export function cleanupUserPrompt(input: string): string {
  if (!input) return input;
  return formatForReadability(fixObviousTypos(input));
}

/** @deprecated Prefer cleanupUserPrompt — same behavior. */
export function cleanupPromptSpelling(input: string): string {
  return cleanupUserPrompt(input);
}
